import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ExternalLink, Pencil, Plus, Trash2, X } from "lucide-react";
import { Modal } from "../UI/Modal";
import Button from "../UI/Button";
import { Work, WorkLocation } from "../../types/works";
import { WorkPriorityBadge, WorkStatusBadge, WorkTypeBadge } from "./badges";
import { MetricsGrid } from "./MetricsGrid";
import InvoiceRefs from "../invoices/InvoiceRefs";
import {
  formatDateTime,
  getCustomerName,
  getDriverName,
  getServiceIcon,
  getServiceName,
  getVehicleLabel,
} from "./utils";
import { ServicePicker } from "./driver/ServicePicker";
import { directoryServiceToType } from "../../constants/worksConstants";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { getWork } from "../../store/slices/worksSlice";
import { getServices } from "../../store/slices/servicesSlice";
import { worksAPI } from "../../services/api";

interface WorkDetailModalProps {
  isOpen: boolean;
  work: Work | null;
  onClose: () => void;
  onEdit: (work: Work) => void;
  onDelete: (work: Work) => void;
  /** Called after a post-completion service add/delete so the list can refresh. */
  onServicesChanged?: () => void;
}

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-[10px] font-medium uppercase tracking-wide text-[#8A9590] mb-0.5 truncate">{label}</div>
    <div className="text-[13px] text-[#2B2F2E] font-medium leading-snug truncate">{value}</div>
  </div>
);

const LocationValue: React.FC<{ location?: WorkLocation; t: (key: string) => string }> = ({ location, t }) => {
  const name = location?.name || location?.address || "—";
  const hasCoords = location?.latitude != null && location?.longitude != null;
  return (
    <span className="inline-flex items-center gap-1.5">
      {name}
      {hasCoords && (
        <a
          href={`https://www.google.com/maps?q=${location!.latitude},${location!.longitude}`}
          target="_blank"
          rel="noopener noreferrer"
          title={t("form.openInMaps")}
          onClick={(e) => e.stopPropagation()}
          className="text-[#8A9590] hover:text-[#C97A3C] transition-colors shrink-0"
        >
          <ExternalLink size={13} />
        </a>
      )}
    </span>
  );
};

export const WorkDetailModal: React.FC<WorkDetailModalProps> = ({
  isOpen,
  work,
  onClose,
  onEdit,
  onDelete,
  onServicesChanged,
}) => {
  const { t } = useTranslation("works");
  const dispatch = useAppDispatch();
  const { isLoaded: servicesLoaded, services: directoryServices } =
    useAppSelector((state) => state.services);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [serviceBusy, setServiceBusy] = useState(false);

  // A completed work is read-only — except its services, which the dispatcher
  // may still manage from the system side (post-completion billable extras).
  const isCompleted = work?.status === "completed";
  const canManageServices = !!work && isCompleted;

  useEffect(() => {
    if (isOpen && canManageServices && !servicesLoaded) {
      dispatch(getServices({ search: "", page: 1, limit: 1000 }));
    }
  }, [isOpen, canManageServices, servicesLoaded, dispatch]);

  // Same catalogue rule as the driver picker: client-scoped services when the
  // customer has any, otherwise the General bucket.
  const serviceOptions = useMemo(() => {
    const general = directoryServices.filter((s) => s.partnerId == null);
    const customerId = work?.customerId;
    const clientScoped = customerId
      ? directoryServices.filter((s) => s.partnerId === customerId)
      : [];
    const resolved = customerId
      ? clientScoped.length > 0
        ? clientScoped
        : general
      : general;
    return resolved.map(directoryServiceToType);
  }, [directoryServices, work?.customerId]);

  const handleAddService = async (
    serviceTypeId: string,
    quantity: number,
    note?: string,
    extra?: { unitPrice?: number; nameSuffix?: string },
  ) => {
    if (!work) return;
    const svc = serviceOptions.find((s) => s.id === serviceTypeId);
    const name =
      extra?.nameSuffix && svc?.name
        ? `${svc.name} — ${extra.nameSuffix}`
        : svc?.name;
    setServiceBusy(true);
    try {
      await worksAPI.addDispatcherService(work._id, {
        serviceTypeId,
        name,
        unit: svc?.unit,
        unitPrice: extra?.unitPrice ?? svc?.defaultPrice,
        tax: svc?.tax,
        quantity,
        note,
      });
      toast.success(t("detail.serviceAdded"));
      setPickerOpen(false);
      dispatch(getWork(work._id));
      onServicesChanged?.();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t("detail.serviceAddFailed"));
    } finally {
      setServiceBusy(false);
    }
  };

  const handleDeleteService = async (lineId: string) => {
    if (!work) return;
    if (!window.confirm(t("detail.confirmDeleteService"))) return;
    setServiceBusy(true);
    try {
      await worksAPI.deleteDispatcherService(work._id, lineId);
      toast.success(t("detail.serviceDeleted"));
      dispatch(getWork(work._id));
      onServicesChanged?.();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || t("detail.serviceDeleteFailed"),
      );
    } finally {
      setServiceBusy(false);
    }
  };

  if (!work) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={work.workNumber} size="lg">
      <div className="flex-1 overflow-y-auto px-3 py-2.5 sm:p-5 space-y-3 sm:space-y-4">

        {/* Badges */}
        <div className="flex flex-wrap items-center gap-1.5">
          <WorkTypeBadge type={work.type} />
          <WorkStatusBadge status={work.status} />
          <WorkPriorityBadge priority={work.priority} />
        </div>

        {/* Overview */}
        <section className="space-y-1.5">
          <h3 className="text-[10px] font-semibold uppercase tracking-wide text-[#8A9590]">{t("detail.overview")}</h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
            <InfoRow label={t("form.fields.customer")} value={getCustomerName(work)} />
            <InfoRow label={t("form.fields.driver")} value={getDriverName(work)} />
            <InfoRow
              label={work.secondaryObject ? t("form.fields.originObject") : t("form.fields.object")}
              value={<LocationValue location={work.secondaryObject ?? work.primaryObject} t={t} />}
            />
            <InfoRow label={t("form.fields.vehicle")} value={getVehicleLabel(work)} />
            {work.secondaryObject && (
              <InfoRow
                label={t("form.fields.destinationObject")}
                value={<LocationValue location={work.primaryObject} t={t} />}
              />
            )}
            {work.material && (
              <InfoRow label={t("form.fields.material")} value={work.material} />
            )}
          </div>
        </section>

        {/* Schedule */}
        <section className="space-y-1.5">
          <h3 className="text-[10px] font-semibold uppercase tracking-wide text-[#8A9590]">{t("detail.schedule")}</h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
            <InfoRow label={t("form.fields.plannedStart")} value={formatDateTime(work.plannedStart)} />
            <InfoRow label={t("form.fields.plannedEnd")} value={formatDateTime(work.plannedEnd)} />
            <InfoRow label={t("detail.actualStart")} value={formatDateTime(work.actualStart)} />
            <InfoRow label={t("detail.actualEnd")} value={formatDateTime(work.actualEnd)} />
          </div>
        </section>

        {/* Metrics */}
        <section className="space-y-1.5">
          <h3 className="text-[10px] font-semibold uppercase tracking-wide text-[#8A9590]">{t("detail.metrics")}</h3>
          <MetricsGrid work={work} />
          {work.type === "cyclic" && (work.roundsPlanned || work.roundsActual) ? (
            <div className="text-xs text-[#2B2F2E] mt-1">
              <span className="text-[#8A9590]">{t("detail.rounds")}:</span>{" "}
              <span className="font-medium">
                {work.roundsActual ?? 0}
              </span>
            </div>
          ) : null}
        </section>

        {/* Services */}
        {(work.services.length > 0 || canManageServices) && (
          <section className="space-y-1.5">
            <div className="flex items-center justify-between">
              <h3 className="text-[10px] font-semibold uppercase tracking-wide text-[#8A9590]">{t("detail.services")}</h3>
              {canManageServices && serviceOptions.length > 0 && (
                <button
                  type="button"
                  disabled={serviceBusy}
                  onClick={() => setPickerOpen(true)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#C97A3C] hover:text-[#B86B30] disabled:opacity-50"
                >
                  <Plus size={13} /> {t("detail.addService")}
                </button>
              )}
            </div>
            <div className="space-y-1">
              {work.services.map((line) => (
                <div
                  key={line.id}
                  className="flex items-center justify-between bg-white border border-[#ECEAE4] rounded-lg px-2.5 py-1.5"
                >
                  <span className="flex items-center gap-1.5 text-xs text-[#2B2F2E] min-w-0">
                    <span>{getServiceIcon(line)}</span>
                    <span className="truncate">{getServiceName(line)}</span>
                    <span
                      className={`shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded ${
                        line.status === "performed"
                          ? "bg-[#E8F3EC] text-[#2F7A4E]"
                          : line.status === "cancelled"
                            ? "bg-[#FBEAEA] text-[#E24B4A]"
                            : "bg-[#E7EFFB] text-[#3B6FB6]"
                      }`}
                    >
                      {t(`detail.serviceStatus.${line.status}`)}
                    </span>
                    {line.invoiceId ? (
                      <Link
                        to={`/invoices/${line.invoiceId}`}
                        onClick={onClose}
                        title={t("detail.viewInvoice")}
                        className="shrink-0 inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#E8F3EC] text-[#2F7A4E] hover:bg-[#D9EBDF] hover:underline"
                      >
                        {t("detail.serviceInvoiced")}
                        {line.invoiceNumber ? ` · ${line.invoiceNumber}` : ""}
                        <ExternalLink size={9} />
                      </Link>
                    ) : (
                      <span className="shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#F1F0EC] text-[#8A9590]">
                        {t("detail.serviceUninvoiced")}
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-[#8A9590] tabular-nums">
                      {line.quantityActual ?? 0} {line.unit}
                    </span>
                    {canManageServices && !line.invoiceId && (
                      <button
                        type="button"
                        disabled={serviceBusy}
                        onClick={() => handleDeleteService(line.id)}
                        title={t("detail.deleteService")}
                        className="text-[#8A9590] hover:text-[#E24B4A] disabled:opacity-50"
                      >
                        <X size={13} />
                      </button>
                    )}
                  </span>
                </div>
              ))}
              {work.services.length === 0 && (
                <p className="text-xs text-[#8A9590]">{t("detail.noServices")}</p>
              )}
            </div>
          </section>
        )}

        {/* Linked invoices */}
        <InvoiceRefs workId={work._id} />

        {/* Remarks */}
        {work.remarks && (
          <section className="space-y-1">
            <h3 className="text-[10px] font-semibold uppercase tracking-wide text-[#8A9590]">{t("form.fields.remarks")}</h3>
            <p className="text-xs text-[#5A6460] bg-white border border-[#ECEAE4] rounded-lg px-2.5 py-1.5 leading-relaxed">
              {work.remarks}
            </p>
          </section>
        )}

        {/* Audit timestamps */}
        <div className="text-[10px] text-[#8A9590] flex flex-wrap gap-x-4 gap-y-0.5 pt-2 border-t border-[#ECEAE4]">
          <span>{t("detail.created")}: {formatDateTime(work.createdAt)}</span>
          <span>{t("detail.updated")}: {formatDateTime(work.updatedAt)}</span>
          <span>{t("detail.createdBy")}: {t(`createdByRole.${work.createdByRole}`)}</span>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 px-3 py-2 sm:px-5 sm:py-3 border-t border-[rgba(11,15,14,0.08)] bg-white shrink-0">
        <Button variant="ghost" onClick={() => onDelete(work)} className="!text-[#E24B4A] hover:!bg-[#FBEAEA]">
          <Trash2 size={15} className="mr-1 inline" /> {t("actions.delete")}
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onClose}>
            {t("form.cancel")}
          </Button>
          {!isCompleted && (
            <Button variant="primary" onClick={() => onEdit(work)}>
              <Pencil size={15} className="mr-1 inline" /> {t("actions.edit")}
            </Button>
          )}
        </div>
      </div>

      {/* Post-completion service add — same picker the driver flow uses. */}
      <ServicePicker
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        serviceTypes={serviceOptions}
        onAdd={handleAddService}
      />
    </Modal>
  );
};
